import React, { useEffect, useMemo, useRef, useState } from 'react';
import { E, Position, httpClient, ordersManager, usePortfolio, T, wsClient } from '../../logic/imports';
import { hideDimmer, showDimmer } from '../app/Dimmer';
import { isWithinUsMarketHours } from '../../shared/utils';

interface ScannerAlertPayload {
    ticker: string;
    side: 'CALL' | 'PUT';
    clear: string;
    grade: string;
}

interface ScannerAlertResponse {
    message?: string;
    data?: {
        tradeId?: string;
        ticker?: string;
        side?: string;
        contractSymbol?: string;
        contractStrike?: number | string;
        contractExpiration?: string;
        entryUnderlyingPrice?: number | string;
        entryContractPrice?: number | string;
    };
}

type ScannerTrackerRow = {
    tradeId: string;
    ticker?: string;
    side?: string;
    contractSymbol?: string;
    contractStrike?: number | string;
    contractExpiration?: string;
    entryUnderlyingPrice?: number | string;
    entryContractPrice?: number | string;
    currentContractPrice?: number | string;
    pnlPercent?: number | string;
    minPnlPercent?: number | string;
    maxPnlPercent?: number | string;
    enteredAt: number;
};

const asNumber = (value: number | string | undefined): number | null => {
    if (value === undefined || value === null || value === '') return null;
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
};

const formatFixed = (value: number | string | undefined, digits: number): string => {
    const n = asNumber(value);
    return n === null ? '-' : n.toFixed(digits);
};

const formatPercent = (value: number | string | undefined): string => {
    const n = asNumber(value);
    return n === null ? '-' : `${n.toFixed(2)}%`;
};

const pnlColor = (value: number | string | undefined): string | undefined => {
    const n = asNumber(value);
    if (n === null) return undefined;
    if (n > 0) return '#2ecc71';
    if (n < 0) return '#ff6b6b';
    return undefined;
};

const formatTimestamp = (ms: number): string => {
    const d = new Date(ms);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const parseScannerInput = (rawInput: string): ScannerAlertPayload => {
    const parts = rawInput.trim().split(/\s+/);
    if (parts.length !== 4) {
        throw new Error('Expected 4 space-separated values: TICKER TYPE CLEAR GRADE');
    }

    const [tickerPart, typePart, clearPart, gradePart] = parts;
    const ticker = tickerPart.trim().toUpperCase();
    const type = typePart.trim().toUpperCase();
    const clear = clearPart.trim();
    const grade = gradePart.trim().toUpperCase();

    if (!ticker || !type || !clear || !grade) {
        throw new Error('All 4 values are required.');
    }

    if (type !== 'C' && type !== 'P' && type !== 'CALL' && type !== 'PUT') {
        throw new Error("Type must be 'C' or 'P' (or CALL/PUT).");
    }

    const side: 'CALL' | 'PUT' = type.startsWith('C') ? 'CALL' : 'PUT';

    return { ticker, side, clear, grade };
};

export const ScannerPage: React.FC = () => {
    const { positions } = usePortfolio();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [input, setInput] = useState('');
    const [riskAmount, setRiskAmount] = useState<string>('100');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [joiningTradeId, setJoiningTradeId] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [responseData, setResponseData] = useState<ScannerAlertResponse['data'] | null>(null);
    const [trackerRows, setTrackerRows] = useState<ScannerTrackerRow[]>([]);
    const [joinedSymbols, setJoinedSymbols] = useState<string[]>([]);

    const upsertTrackerRow = (incoming: Partial<ScannerTrackerRow> & { tradeId: string }) => {
        setTrackerRows(prev => {
            const idx = prev.findIndex(r => r.tradeId === incoming.tradeId);
            const existing = idx >= 0 ? prev[idx] : null;
            const merged: ScannerTrackerRow = {
                ...(existing || { tradeId: incoming.tradeId, enteredAt: Date.now() }),
                ...incoming,
                enteredAt: existing?.enteredAt ?? Date.now(),
            };

            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = merged;
                return copy.sort((a, b) => b.enteredAt - a.enteredAt);
            }

            return [merged, ...prev].sort((a, b) => b.enteredAt - a.enteredAt);
        });
    };

    useEffect(() => {
        const previous = wsClient.onScannerAlertUpdate;

        wsClient.onScannerAlertUpdate = (push: T.ScannerAlertWsPayload) => {
            const updates = Array.isArray(push) ? push : [push as unknown as T.ScannerTradeWsUpdate];

            updates.forEach((update) => {
                upsertTrackerRow({
                    tradeId: update.tradeId,
                    ticker: update.ticker,
                    side: update.side,
                    contractSymbol: update.contractSymbol,
                    contractStrike: update.contractStrike,
                    contractExpiration: update.contractExpiration,
                    currentContractPrice: update.currentContractPrice,
                    pnlPercent: update.pnlPercent,
                    minPnlPercent: update.minPnlPercent,
                    maxPnlPercent: update.maxPnlPercent,
                });
            });

            previous?.(updates);
        };

        return () => {
            wsClient.onScannerAlertUpdate = previous;
        };
    }, []);

    const parsedPreview = useMemo(() => {
        try {
            if (!input.trim()) return null;
            return parseScannerInput(input);
        } catch {
            return null;
        }
    }, [input]);

    const scannerPositions = useMemo(() => {
        const symbols = new Set(joinedSymbols);
        const rows = trackerRows.map(r => r.contractSymbol).filter(Boolean) as string[];
        rows.forEach(s => symbols.add(s));

        if (!positions || positions.size === 0 || symbols.size === 0) return [] as Position[];

        const out: Position[] = [];
        positions.forEach((pos) => {
            if (symbols.has(pos.option.symbol)) out.push(pos);
        });

        return out.sort((a, b) => (b.openedAt || '').localeCompare(a.openedAt || ''));
    }, [positions, joinedSymbols, trackerRows]);

    const getContractsFromRisk = (unitPrice: number): number => {
        const risk = Number(riskAmount);
        if (!Number.isFinite(risk) || risk <= 0) return 0;
        if (!Number.isFinite(unitPrice) || unitPrice <= 0) return 0;
        return Math.floor(risk / (unitPrice * 100));
    };

    const placeScannerOrder = async (
        instrument: T.Option,
        side: E.OrderSide,
        quantity: number,
        dimmerLabel: string
    ) => {
        const order: any = {
            id: crypto.randomUUID(),
            brokerageId: undefined,
            instrument,
            status: E.OrderStatus.WORKING,
            side,
            type: E.OrderType.MARKET,
            quantity,
            isManaged: false,
            timeInForce: E.TimeInForce.DAY
        };

        showDimmer(dimmerLabel, false);
        await ordersManager.placeOrder(order);
        hideDimmer();
    };

    const onJoinTrade = async (row: ScannerTrackerRow) => {
        if (joiningTradeId) return;
        if (!row.contractSymbol || !row.ticker || !row.side) {
            showDimmer('Trade data incomplete', true);
            return;
        }

        const refPrice = asNumber(row.currentContractPrice) ?? asNumber(row.entryContractPrice);
        if (!refPrice) {
            showDimmer('No contract price available', true);
            return;
        }

        const contracts = getContractsFromRisk(refPrice);
        if (contracts <= 0) {
            showDimmer('Risk amount too low for contract price', true);
            return;
        }

        const side = row.side.toUpperCase() === 'PUT' ? E.OptionType.PUT : E.OptionType.CALL;
        const option: T.Option = {
            kind: 'option',
            symbol: row.contractSymbol,
            name: row.contractSymbol,
            type: side,
            expiration: row.contractExpiration || '',
            strike: asNumber(row.contractStrike) || 0,
            underlying: {
                kind: 'stock',
                symbol: row.ticker,
                name: row.ticker,
            }
        };

        try {
            setJoiningTradeId(row.tradeId);
            await placeScannerOrder(option, E.OrderSide.BUY, contracts, `Joining ${row.ticker} trade...`);
            setJoinedSymbols(prev => prev.includes(row.contractSymbol!) ? prev : [...prev, row.contractSymbol!]);
        } catch (error: any) {
            showDimmer('Join trade failed: ' + (error?.message || 'Unknown error'), true);
        } finally {
            setJoiningTradeId(null);
        }
    };

    const onSellFraction = async (position: Position, fraction: 'HALF' | 'QUARTER' | 'ALL') => {
        const available = position.contractsAvailable > 0 ? position.contractsAvailable : position.contractsHeld;
        if (available <= 0) {
            showDimmer('No contracts available to sell', true);
            return;
        }

        let quantity = available;
        if (fraction === 'HALF') quantity = Math.max(1, Math.floor(available / 2));
        if (fraction === 'QUARTER') quantity = Math.max(1, Math.floor(available / 4));

        try {
            await placeScannerOrder(position.option, E.OrderSide.SELL, quantity, 'Submitting sell order...');
        } catch (error: any) {
            showDimmer('Sell failed: ' + (error?.message || 'Unknown error'), true);
        }
    };

    const onSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (isSubmitting) return;

        setErrorMessage(null);

        let payload: ScannerAlertPayload;
        try {
            payload = parseScannerInput(input);
        } catch (err: any) {
            setResponseData(null);
            setErrorMessage(err?.message || 'Invalid scanner input.');
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await httpClient.submitScannerAlert(payload);
            const json: ScannerAlertResponse = {
                message: result.message,
                data: result.data,
            };

            if (json.data?.tradeId) {
                upsertTrackerRow({
                    tradeId: json.data.tradeId,
                    ticker: json.data.ticker,
                    side: json.data.side,
                    contractSymbol: json.data.contractSymbol,
                    contractStrike: json.data.contractStrike,
                    contractExpiration: json.data.contractExpiration,
                    entryUnderlyingPrice: json.data.entryUnderlyingPrice,
                    entryContractPrice: json.data.entryContractPrice,
                });

                setInput('');
                inputRef.current?.focus();
            }

            setResponseData(json.data || null);
        } catch (err: any) {
            setResponseData(null);
            setErrorMessage(err?.message || 'Request failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="page">
            {!isWithinUsMarketHours() && (
                <div className='message-box message-box--warning' style={{
                    width: '90%',
                    minWidth: '400px',
                    margin: '0 auto',
                    marginBottom: '10px'
                }}>
                    The Market is CLOSED.
                </div>
            )}

            <form className="panel" onSubmit={onSubmit}>
                <div className="panel-header">
                    Alert Input
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Enter alert, e.g. SPY C 87 B"
                        style={{ flex: '1 1 360px', minHeight: '34px', padding: '0 8px' }}
                        disabled={isSubmitting}
                    />
                    <button
                        type="submit"
                        className="button-outlined"
                        disabled={isSubmitting || !input.trim()}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
            </form>

            {errorMessage && (
                <div className="message-box message-box--warning" style={{ width: '90%', maxWidth: '900px', margin: '10px auto 0 auto' }}>
                    {errorMessage}
                </div>
            )}

            {responseData && (
                <div className="panel">
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Response</div>
                    <pre style={{ margin: 0, overflowX: 'auto', fontSize: '0.85em' }}>
                        {JSON.stringify(responseData, null, 2)}
                    </pre>
                </div>
            )}

            <div className="panel">
                <div className="panel-header">
                    Risk Amount
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>$</span>
                    <input
                        type="number"
                        min="1"
                        step="1"
                        value={riskAmount}
                        onChange={(e) => setRiskAmount(e.target.value)}
                        style={{ width: '140px', minHeight: '30px', padding: '0 8px' }}
                    />
                    <span style={{ opacity: 0.8, fontSize: '0.9em' }}>
                        Used to size contracts when you click JOIN TRADE.
                    </span>
                </div>
            </div>

            <div className="panel">
                <div className="panel-header">
                    Live Scanner Tracker
                </div>

                {trackerRows.length === 0 ? (
                    <div style={{ opacity: 0.8 }}>No tracked ideas yet.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '6px' }}>Timestamp</th>
                                    <th style={{ textAlign: 'left', padding: '6px' }}>Ticker</th>
                                    <th style={{ textAlign: 'left', padding: '6px' }}>Side</th>
                                    <th style={{ textAlign: 'right', padding: '6px' }}>PnL %</th>
                                    <th style={{ textAlign: 'right', padding: '6px' }}>Min %</th>
                                    <th style={{ textAlign: 'right', padding: '6px' }}>Max %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trackerRows.map((row) => (
                                    <React.Fragment key={row.tradeId}>
                                        <tr>
                                            <td style={{ padding: '6px', borderTop: '1px solid #6f8193', borderLeft: '1px solid #6f8193', borderTopLeftRadius: '6px', background: 'rgba(255,255,255,0.03)' }}>{formatTimestamp(row.enteredAt)}</td>
                                            <td style={{ padding: '6px', borderTop: '1px solid #6f8193', background: 'rgba(255,255,255,0.03)' }}>{row.ticker || '-'}</td>
                                            <td style={{ padding: '6px', borderTop: '1px solid #6f8193', background: 'rgba(255,255,255,0.03)' }}>{row.side || '-'}</td>
                                            <td style={{ padding: '6px', textAlign: 'right', color: pnlColor(row.pnlPercent), borderTop: '1px solid #6f8193', background: 'rgba(255,255,255,0.03)' }}>{formatPercent(row.pnlPercent)}</td>
                                            <td style={{ padding: '6px', textAlign: 'right', color: pnlColor(row.minPnlPercent), borderTop: '1px solid #6f8193', background: 'rgba(255,255,255,0.03)' }}>{formatPercent(row.minPnlPercent)}</td>
                                            <td style={{ padding: '6px', textAlign: 'right', color: pnlColor(row.maxPnlPercent), borderTop: '1px solid #6f8193', borderRight: '1px solid #6f8193', borderTopRightRadius: '6px', background: 'rgba(255,255,255,0.03)' }}>{formatPercent(row.maxPnlPercent)}</td>
                                        </tr>
                                        <tr>
                                            <td colSpan={6} style={{ padding: '6px', borderLeft: '1px solid #6f8193', borderRight: '1px solid #6f8193', borderBottom: '1px solid #6f8193', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', background: 'rgba(255,255,255,0.03)' }}>
                                                <button
                                                    className="button-outlined"
                                                    style={{ width: '100%', minHeight: '28px', padding: '4px 8px', fontSize: '0.82em' }}
                                                    onClick={() => onJoinTrade(row)}
                                                    disabled={joiningTradeId === row.tradeId}
                                                >
                                                    {joiningTradeId === row.tradeId ? 'JOINING...' : 'JOIN TRADE'}
                                                </button>
                                            </td>
                                        </tr>
                                        <tr aria-hidden="true">
                                            <td colSpan={6} style={{ height: '6px', padding: 0, border: 'none', background: 'transparent' }} />
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="panel">
                <div className="panel-header">
                    Your Positions
                </div>
                {scannerPositions.length === 0 ? (
                    <div style={{ opacity: 0.8 }}>No scanner positions yet.</div>
                ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                        {scannerPositions.map((pos) => {
                            const pnl = pos.stats && pos.stats.totalCost > 0
                                ? (pos.stats.openPnL / pos.stats.totalCost) * 100
                                : 0;

                            return (
                                <div key={pos.id} style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr auto auto',
                                    gap: '8px',
                                    alignItems: 'center',
                                    background: 'rgba(255,255,255,0.05)',
                                    borderRadius: '6px',
                                    padding: '8px'
                                }}>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <span style={{ fontWeight: 'bold' }}>{pos.option.underlying?.symbol || '-'}</span>
                                        <span>{pos.option.type || '-'}</span>
                                        <span style={{ color: pnlColor(pnl) }}>{formatPercent(pnl)}</span>
                                    </div>

                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <button className="strike-limit-button" style={{ padding: '4px 8px' }} onClick={() => onSellFraction(pos, 'HALF')}>
                                            1/2
                                        </button>
                                        <button className="strike-limit-button" style={{ padding: '4px 8px' }} onClick={() => onSellFraction(pos, 'QUARTER')}>
                                            1/4
                                        </button>
                                        <button className="strike-limit-button" style={{ padding: '4px 8px' }} onClick={() => onSellFraction(pos, 'ALL')}>
                                            ALL
                                        </button>
                                    </div>
                                    <div style={{ opacity: 0.75, fontSize: '0.85em', textAlign: 'right' }}>
                                        {pos.contractsHeld} ctr
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

        </div>
    );
}